import React, { useState } from 'react';
import { auth } from '../firebase';

const PhoneLoginTest = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Test functions
  const addTestResult = (test, result, details = '') => {
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setTestResults(prev => [...prev, {
      id: uniqueId,
      test,
      result,
      details,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Countdown timer
  React.useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Test 1: Phone number validation
  const testPhoneValidation = () => {
    const phoneRegex = /^(\+98|0)?9[0-9]{9}$/;
    const isValid = phoneRegex.test(phone);
    
    addTestResult(
      'Phone Validation',
      isValid ? '✅ PASS' : '❌ FAIL',
      `Phone: ${phone}, Valid: ${isValid}`
    );
    
    return isValid;
  };

  // Test 2: Format phone number
  const formatPhoneNumber = (phone) => {
    let formatted = phone;
    
    // Remove spaces and dashes
    formatted = formatted.replace(/[\s\-]/g, '');
    
    // Add +98 if starts with 0
    if (formatted.startsWith('0')) {
      formatted = '+98' + formatted.substring(1);
    }
    
    // Add +98 if starts with 9
    if (formatted.startsWith('9')) {
      formatted = '+98' + formatted;
    }
    
    // Add +98 if doesn't start with +
    if (!formatted.startsWith('+')) {
      formatted = '+98' + formatted;
    }
    
    return formatted;
  };

  // Test 3: Phone formatting
  const testPhoneFormatting = () => {
    const formatted = formatPhoneNumber(phone);
    const expectedFormat = /^\+98[0-9]{10}$/;
    const isValid = expectedFormat.test(formatted);
    
    addTestResult(
      'Phone Formatting',
      isValid ? '✅ PASS' : '❌ FAIL',
      `Original: ${phone}, Formatted: ${formatted}`
    );
    
    return { isValid, formatted };
  };

  // Test 4: Send OTP
  const testSendOTP = async () => {
    if (!phone.trim()) {
      setMessage('لطفاً شماره تلفن را وارد کنید');
      return;
    }

    setLoading(true);
    setMessage('در حال ارسال کد تایید...');
    
    try {
      const formatted = formatPhoneNumber(phone);
      
      const response = await fetch('https://empty-rain-2654.er-hooman.workers.dev/api/firebase/sendOTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: formatted })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        addTestResult(
          'Send OTP',
          '✅ PASS',
          `OTP sent to ${result.phone}`
        );
        setMessage('✅ کد تایید ارسال شد!');
        setOtpSent(true);
        setCountdown(120); // 2 minutes countdown
      } else {
        addTestResult(
          'Send OTP',
          '❌ FAIL',
          result.error || 'Failed to send OTP'
        );
        setMessage(`❌ خطا: ${result.error || 'خطا در ارسال کد'}`);
      }
    } catch (error) {
      addTestResult(
        'Send OTP',
        '❌ ERROR',
        `Network error: ${error.message}`
      );
      setMessage('❌ خطای شبکه!');
    } finally {
      setLoading(false);
    }
  };

  // Test 5: Verify OTP
  const testVerifyOTP = async () => {
    if (!phone.trim() || !otp.trim()) {
      setMessage('لطفاً شماره تلفن و کد تایید را وارد کنید');
      return;
    }

    setLoading(true);
    setMessage('در حال تایید کد...');
    
    try {
      const formatted = formatPhoneNumber(phone);
      
      const response = await fetch('https://empty-rain-2654.er-hooman.workers.dev/api/firebase/verifyOTP', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phone: formatted,
          otp: otp.trim()
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        addTestResult(
          'Verify OTP',
          '✅ PASS',
          `${result.message} - User ID: ${result.userId}`
        );
        setMessage(`✅ ${result.message}!`);
        
        // Sign in with custom token
        if (result.token) {
          try {
            await auth.signInWithCustomToken(result.token);
            addTestResult(
              'Firebase Auth',
              '✅ PASS',
              'User signed in successfully'
            );
            setMessage('✅ ورود موفق! کاربر وارد شد.');
          } catch (authError) {
            addTestResult(
              'Firebase Auth',
              '❌ FAIL',
              `Auth error: ${authError.message}`
            );
            setMessage('❌ خطا در ورود به Firebase!');
          }
        }
      } else {
        addTestResult(
          'Verify OTP',
          '❌ FAIL',
          result.error || 'Failed to verify OTP'
        );
        setMessage(`❌ خطا: ${result.error || 'خطا در تایید کد'}`);
      }
    } catch (error) {
      addTestResult(
        'Verify OTP',
        '❌ ERROR',
        `Network error: ${error.message}`
      );
      setMessage('❌ خطای شبکه!');
    } finally {
      setLoading(false);
    }
  };

  // Test 6: Test Kavenegar API
  const testKavenegarAPI = async () => {
    setLoading(true);
    setMessage('در حال تست کاوه‌نگار API...');
    
    try {
      const response = await fetch('https://empty-rain-2654.er-hooman.workers.dev/api/firebase/testKavenegar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        addTestResult(
          'Kavenegar API Test',
          '✅ PASS',
          'API connection successful'
        );
        setMessage('✅ کاوه‌نگار API کار می‌کند!');
      } else {
        addTestResult(
          'Kavenegar API Test',
          '❌ FAIL',
          result.error || 'API test failed'
        );
        setMessage(`❌ خطا: ${result.error || 'خطا در تست API'}`);
      }
    } catch (error) {
      addTestResult(
        'Kavenegar API Test',
        '❌ ERROR',
        `Network error: ${error.message}`
      );
      setMessage('❌ خطای شبکه!');
    } finally {
      setLoading(false);
    }
  };

  // Test 7: Complete flow test
  const testCompleteFlow = async () => {
    setTestResults([]);
    setMessage('در حال تست کامل فرآیند...');
    
    // Test 1: Phone validation
    const phoneValid = testPhoneValidation();
    
    // Test 2: Phone formatting
    const { isValid: formatValid, formatted } = testPhoneFormatting();
    
    // Test 3: Send OTP (if validations pass)
    if (phoneValid && formatValid) {
      await testSendOTP();
    } else {
      addTestResult(
        'Send OTP',
        '⏭️ SKIPPED',
        'Skipped due to validation failures'
      );
    }
    
    setMessage('✅ تست کامل اجرا شد!');
  };

  // Clear test results
  const clearResults = () => {
    setTestResults([]);
    setMessage('');
    setOtpSent(false);
    setCountdown(0);
    setOtp('');
  };

  // Resend OTP
  const resendOTP = () => {
    if (countdown === 0) {
      testSendOTP();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-center mb-6">تست لاگین با شماره تلفن + OTP</h1>
          
          {/* Test Data Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">📋 راهنمای تست:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• شماره تلفن معتبر وارد کنید (مثل: <code className="bg-blue-100 px-2 py-1 rounded">09123456789</code>)</li>
              <li>• روی "ارسال کد تایید" کلیک کنید</li>
              <li>• کد 6 رقمی ارسال شده را وارد کنید</li>
              <li>• روی "تایید کد" کلیک کنید</li>
              <li>• در صورت موفقیت، کاربر در Firebase ایجاد/احراز هویت می‌شود</li>
            </ul>
          </div>

          {/* Input Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                شماره تلفن:
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="09123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                کد تایید:
              </label>
              <div className="flex space-x-2 space-x-reverse">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading || !otpSent}
                />
                {countdown > 0 && (
                  <span className="px-3 py-2 text-sm text-gray-500">
                    {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <button
              onClick={testPhoneValidation}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              تست اعتبارسنجی تلفن
            </button>
            
            <button
              onClick={testPhoneFormatting}
              disabled={loading}
              className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors disabled:opacity-50"
            >
              تست فرمت تلفن
            </button>
            
            <button
              onClick={testKavenegarAPI}
              disabled={loading}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors disabled:opacity-50"
            >
              تست کاوه‌نگار API
            </button>
            
            <button
              onClick={testSendOTP}
              disabled={loading || !phone.trim()}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'در حال ارسال...' : 'ارسال کد تایید'}
            </button>
            
            <button
              onClick={testCompleteFlow}
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              تست کامل فرآیند
            </button>
          </div>

          {/* OTP Verification Button */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <button
              onClick={testVerifyOTP}
              disabled={loading || !otp.trim() || !otpSent}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'در حال تایید...' : 'تایید کد'}
            </button>
            
            <button
              onClick={resendOTP}
              disabled={loading || countdown > 0}
              className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors disabled:opacity-50"
            >
              ارسال مجدد کد
            </button>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`p-3 rounded-md text-sm mb-4 ${
              message.includes('✅') ? 'bg-green-100 text-green-700' : 
              message.includes('❌') ? 'bg-red-100 text-red-700' : 
              'bg-blue-100 text-blue-700'
            }`}>
              {message}
            </div>
          )}

          {/* Clear Results Button */}
          <div className="text-center">
            <button
              onClick={clearResults}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              پاک کردن نتایج
            </button>
          </div>
        </div>

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">نتایج تست‌ها:</h2>
            <div className="space-y-3">
              {testResults.map((result) => (
                <div key={result.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold">{result.test}:</span>
                      <span className={`ml-2 ${
                        result.result.includes('✅') ? 'text-green-600' :
                        result.result.includes('❌') ? 'text-red-600' :
                        'text-orange-600'
                      }`}>
                        {result.result}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{result.timestamp}</span>
                  </div>
                  {result.details && (
                    <div className="text-sm text-gray-600 mt-1">
                      {result.details}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneLoginTest;
